import { Box, Paper, Popover } from '@mui/material';
import React from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import en_ja from '../../translations/en_ja';

const Calendar = ({ open, anchorEl, onClose, selectedDate, onDateChange }) => {
    const handleDateSelect = (selectInfo) => {
        onDateChange(new Date(selectInfo.start));
        onClose();
    };

    const isSameDate = (date1, date2) => {
        return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
        );
    };

    const getDayCellClassNames = (arg) => {
        const cellDate = new Date(arg.date);
        const today = new Date();

        if (
            selectedDate &&
            isSameDate(cellDate, selectedDate) &&
            !isSameDate(cellDate, today)
        ) {
            return ['fc-day-selected-custom'];
        }
        return [];
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{
                mt: 1,
                '& .MuiPopover-paper': {
                    borderRadius: '16px',
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 8px 32px rgba(0,0,0,0.08))',
                    '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: -8,
                        left: '50%',
                        width: 16,
                        height: 16,
                        backgroundColor: 'white',
                        transform: 'translateX(-50%) rotate(45deg)',
                        zIndex: 0,
                    }
                }
            }}
        >
            <Paper
                sx={{
                    p: 0,
                    width: 380,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.06)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)'
                }}
            >
                <Box sx={{
                    // Global FullCalendar reset
                    '& .fc': {
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        fontSize: '14px',
                        lineHeight: 1.4
                    },

                    // Header styling - Skyscanner style
                    '& .fc-header-toolbar': {
                        padding: '20px 24px 16px',
                        margin: 0,
                        backgroundColor: 'white',
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    },

                    '& .fc-toolbar-title': {
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#1a1a1a',
                        margin: 0,
                        letterSpacing: '-0.02em'
                    },

                    // Navigation buttons - modern style
                    '& .fc-button-group': {
                        display: 'flex',
                        gap: '8px'
                    },

                    '& .fc-button': {
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#6b7280',
                        padding: '8px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: 500,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        minWidth: 'auto',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                            backgroundColor: '#f3f4f6',
                            color: '#1f2937',
                            transform: 'scale(1.05)'
                        },
                        '&:focus': {
                            boxShadow: 'none',
                            outline: 'none'
                        },
                        '&:active': {
                            transform: 'scale(0.98)'
                        }
                    },

                    '& .fc-today-button': {
                        backgroundColor: '#dc3545',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: 600,
                        padding: '6px 12px',
                        borderRadius: '8px',
                        width: 'auto',
                        minWidth: '60px',
                        '&:hover': {
                            backgroundColor: '#c82333',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)'
                        }
                    },

                    // Calendar body
                    '& .fc-daygrid': {
                        padding: '0 24px 20px'
                    },

                    // Day headers - clean modern style
                    '& .fc-col-header': {
                        backgroundColor: 'transparent',
                        borderBottom: 'none',
                        padding: '16px 0 12px'
                    },

                    '& .fc-col-header-cell': {
                        border: 'none',
                        padding: 0
                    },

                    '& .fc-col-header-cell-cushion': {
                        color: '#9ca3af',
                        fontWeight: 600,
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        padding: '0',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    },

                    // Day cells - premium styling
                    '& .fc-daygrid-day': {
                        border: 'none',
                        position: 'relative',
                        padding: '2px'
                    },

                    '& .fc-daygrid-day-frame': {
                        minHeight: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                    },

                    '& .fc-daygrid-day-number': {
                        color: '#374151',
                        fontSize: '15px',
                        fontWeight: 500,
                        textDecoration: 'none',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '12px',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        zIndex: 1,
                        '&:hover': {
                            backgroundColor: '#f8fafc',
                            color: '#1f2937',
                            transform: 'scale(1.05)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }
                    },

                    // Today styling - highlighted
                    '& .fc-day-today': {
                        backgroundColor: 'transparent',
                        '& .fc-daygrid-day-number': {
                            backgroundColor: '#dc3545',
                            color: 'white',
                            fontWeight: 700,
                            boxShadow: '0 4px 12px rgba(220, 53, 69, 0.4)',
                            '&:hover': {
                                backgroundColor: '#c82333',
                                transform: 'scale(1.1)',
                                boxShadow: '0 6px 16px rgba(220, 53, 69, 0.5)'
                            }
                        }
                    },

                    // Selected day styling
                    '& .fc-day-selected': {
                        '& .fc-daygrid-day-number': {
                            backgroundColor: '#1f2937',
                            color: 'white',
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(31, 41, 55, 0.3)'
                        }
                    },

                    '& .fc-day.fc-day-selected-custom': {
                        '& .fc-daygrid-day-number': {
                            backgroundColor: '#e3f2fd', // Light blue background
                            color: '#1565c0', // Darker blue text
                            fontWeight: 600,
                            boxShadow: '0 2px 6px rgba(21, 101, 192, 0.2)',
                            '&:hover': {
                                backgroundColor: '#bbdefb',
                                transform: 'scale(1.05)',
                                boxShadow: '0 3px 10px rgba(21, 101, 192, 0.3)'
                            }
                        }
                    },

                    '& .fc-day-sat:not(.fc-day-today) .fc-daygrid-day-number, \
                            & .fc-day-sun:not(.fc-day-today) .fc-daygrid-day-number': {
                        color: '#dc3545',
                        '&:hover': {
                            backgroundColor: '#fef2f2',
                            color: '#dc2626',
                        },
                    },

                    // Other month dates - subtle
                    '& .fc-day-other': {
                        '& .fc-daygrid-day-number': {
                            color: '#d1d5db',
                            '&:hover': {
                                backgroundColor: '#f9fafb',
                                color: '#9ca3af'
                            }
                        }
                    },

                    // Remove all default borders and backgrounds
                    '& .fc-scrollgrid': {
                        border: 'none'
                    },

                    '& .fc-scrollgrid-section > *': {
                        border: 'none'
                    },

                    '& .fc-scrollgrid-section-header > *': {
                        borderBottom: 'none'
                    },

                    '& .fc-scrollgrid-section-body > *': {
                        border: 'none'
                    },

                    // Hover effects for rows
                    '& .fc-daygrid-week': {
                        '&:hover': {
                            '& .fc-daygrid-day': {
                                backgroundColor: 'rgba(248, 250, 252, 0.5)'
                            }
                        }
                    },

                    // Custom animations
                    '& .fc-daygrid-day-events': {
                        display: 'none'
                    },
                }}>

                    <FullCalendar
                        plugins={[dayGridPlugin, interactionPlugin]}
                        initialView="dayGridMonth"
                        selectable={true}
                        selectMirror={true}
                        dayMaxEvents={false}
                        weekends={true}
                        select={handleDateSelect}
                        dayCellClassNames={getDayCellClassNames}
                        initialDate={selectedDate}
                        headerToolbar={{
                            left: 'prev',
                            center: 'title',
                            right: 'next today'
                        }}
                        height="auto"
                        aspectRatio={1.4}
                        buttonText={{
                            today: en_ja["Today"]
                        }}
                        buttonIcons={{
                            prev: 'chevron-left',
                            next: 'chevron-right'
                        }}
                        dayHeaderFormat={{ weekday: 'short' }}
                        titleFormat={{ year: 'numeric', month: 'long' }}
                        fixedWeekCount={false}
                        showNonCurrentDates={true}
                        dayMaxEventRows={false}
                        eventDisplay="block"
                        displayEventTime={false}
                    />
                </Box>
            </Paper>
        </Popover>
    );
};

export default Calendar;
